import React, { Suspense, useState, useEffect, useMemo } from 'react';
import { Container, CircularProgress, Grid, Box, Fade, Typography } from '@mui/material';
import { useGetBuildingsQuery } from '../../../api/recalc/endpoints/buildingApiSlice';
import { useGetAccountsQuery } from '../../../api/recalc/endpoints/accountsApiSlice';
import { useGetSubscriptionsQuery } from '../../../api/recalc/endpoints/jobSubscriptionApiSlice';
import FilterPanel from './FilterPanel';
import enrichAccounts from '../../../utils/enrichAccounts';
import { useNavigate, useLocation } from 'react-router-dom';

const OperationsCard = React.lazy(() =>
  import('remote/OperationsCard').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const ScrollBodyDialog = React.lazy(() =>
  import('remote/ScrollBodyDialog').catch(() => ({
    default: () => <div>Failed to load</div>,
  }))
);

const groupAccountsById = (accounts, groups) => {
  const grouped = {};

  // Initialize groups with empty arrays
  for (const group in groups) {
    if (groups.hasOwnProperty(group)) {
      grouped[group] = [];
    }
  }

  // Add an "Others" group for ungrouped accounts
  grouped["Others"] = [];

  // Assign accounts to groups based on their IDs
  accounts?.forEach(account => {
    let assigned = false;
    for (const [group, ids] of Object.entries(groups)) {
      if (ids.includes(account.AccountId)) {
        grouped[group].push(account);
        assigned = true;
        break;
      }
    }
    // If the account doesn't match any group, assign it to "Others"
    if (!assigned) {
      grouped["Others"].push(account);
    }
  });

  return grouped;
};

const OperationsPage = () => {
  const [enrichedAccounts, setEnrichedAccounts] = useState([]);
  const [activeFilter, setActiveFilter] = useState(true);
  const [accountGroups, setAccountGroups] = useState(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false); // State for ScrollBodyDialog
  const [clickTimeout, setClickTimeout] = useState(null); // State to manage the click timeout
  const { state } = useLocation();
  const accountGroup = state?.accountGroup || {}; // Default to {} if state is undefined

  useEffect(() => {
    async function fetchJsonData() {
      const data = await import('remote/accountGroups');
      setAccountGroups(data.default);
    }
    fetchJsonData();
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    if (!accountGroup) {
      navigate('/operations', { replace: true });
    }
  }, [accountGroup, navigate]);

  const handleCardDoubleClick = (groupKey, accountGroup) => {
    const encodedGroupKey = encodeURIComponent(groupKey);
    navigate(`/operations/${encodedGroupKey}`, { state: { accountGroup, activeFilter } });
  };

  
  const handleCardClick = (groupKey, accountGroup) => {
    if (clickTimeout) {
      clearTimeout(clickTimeout); // If there's already a click timeout, clear it
      setClickTimeout(null); // Reset the click timeout
    } else {
      const timeout = setTimeout(() => {
        // Execute the single click logic after the timeout
        setSelectedGroup(accountGroup);
        setSelectedGroupKey(groupKey);
        setDialogOpen(true); // Open the dialog when a card is clicked
      }, 250); // Delay time to differentiate single and double click (250ms)
      setClickTimeout(timeout); // Store the timeout reference
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false); // Close the dialog
  };

  const { data: buildings, isLoading: isLoadingBuildings, isSuccess: isSuccessBuildings } = useGetBuildingsQuery({
    pollingInterval: 3000,
    skipPollingIfUnfocused: true,
  });
  const { data: accounts, isLoading: isLoadingAccounts, isSuccess: isSuccessAccounts } = useGetAccountsQuery({
    pollingInterval: 3000,
    skipPollingIfUnfocused: true,
  });
  const { data: subscriptions, isLoading: isLoadingSubscriptions, isSuccess: isSuccessSubscriptions } = useGetSubscriptionsQuery({
    pollingInterval: 3000,
    skipPollingIfUnfocused: true,
  });

  // Determine the current loading state
  const loadingMessage = useMemo(() => {
    if (isLoadingAccounts) return 'Loading accounts...';
    if (isLoadingBuildings) return 'Loading buildings...';
    if (isLoadingSubscriptions) return 'Loading subscriptions...';
    return null;
  }, [isLoadingAccounts, isLoadingBuildings, isLoadingSubscriptions]);

  // Memoize enriched accounts
  const enrichedAccountsMemo = useMemo(() => {
    if (!loadingMessage) {
      return enrichAccounts(accounts, buildings, subscriptions);
    }
    return [];
  }, [loadingMessage, accounts, buildings, subscriptions]);

  useEffect(() => {
    if (!loadingMessage) {
      setEnrichedAccounts(enrichedAccountsMemo);
    }
  }, [loadingMessage, enrichedAccountsMemo]);

  const groupedAccounts = useMemo(() => groupAccountsById(enrichedAccounts, accountGroups), [enrichedAccounts]);

  const filteredAccountGroups = useMemo(() => {
    return Object.entries(groupedAccounts).reduce((result, [groupKey, accounts]) => {
      const filteredAccounts = accounts
        .map((account) => {
          const filteredBuildings = account.Buildings.filter((building) =>
            building.JobSubscriptions.some((sub) => !activeFilter || sub.StateCode === 'Active')
          );
          return { ...account, Buildings: filteredBuildings };
        })
        .filter((account) => account.Buildings.length > 0);

      if (filteredAccounts.length > 0) {
        result[groupKey] = filteredAccounts;
      }
      return result;
    }, {});
  }, [groupedAccounts, activeFilter]);

  return (
    <Container maxWidth="xxl"> {/* Adjust margin top to create space below AppBar */}
      <FilterPanel activeFilter={activeFilter} setActiveFilter={setActiveFilter} />
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 4 }}>Getting things ready...</Typography>
          </Box>
        }
      >
        {loadingMessage ? (
          <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', justifyContent: 'center', height: '80vh', verticalAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 4 }}>{loadingMessage}</Typography>
          </Box>
        ) : (
          <Grid container spacing={4} sx={{ mt: 1 }}>
            {Object.entries(filteredAccountGroups)?.map(([groupKey, accounts]) => (
              <Grid item sm={6} md={6} lg={6} xl={3} key={groupKey}>
                <Fade in={true} timeout={500}>
                  <Box>
                    <OperationsCard groupKey={groupKey} accounts={accounts} onCardClick={() => handleCardClick(groupKey, accounts)} onCardDoubleClick={() => handleCardDoubleClick(groupKey, accounts)} activeFilter={activeFilter} />
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}
      </Suspense>
      <ScrollBodyDialog groupKey={selectedGroupKey} accounts={accounts} open={dialogOpen} onClose={handleDialogClose} />
    </Container>
  );
};

export default OperationsPage;